import type { components } from '@trackercop/contracts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

type QueueState = components['schemas']['QueueState'];

interface QueueGaugeProps {
  queue: QueueState;
}

function formatAge(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}

function errorRateVariant(rate: number): 'success' | 'warning' | 'destructive' {
  if (rate < 0.01) return 'success';
  if (rate < 0.05) return 'warning';
  return 'destructive';
}

export function QueueGauge({ queue }: QueueGaugeProps) {
  return (
    <Card data-testid={`queue-gauge-${queue.queueName}`}>
      <CardHeader>
        <CardTitle className="font-mono text-sm">{queue.queueName}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3 text-xs">
        <Stat label="Depth" value={queue.depth.toLocaleString()} />
        <Stat
          label="DLQ depth"
          value={queue.dlqDepth.toLocaleString()}
          highlight={queue.dlqDepth > 0}
        />
        <Stat label="Oldest" value={formatAge(queue.oldestAgeMs)} />
        <Stat
          label="Processed/h"
          value={queue.processedLastHour.toLocaleString()}
        />
        <div className="col-span-2 flex items-center justify-between border-t pt-2">
          <span className="text-muted-foreground">Error rate (1h)</span>
          <Badge variant={errorRateVariant(queue.errorRateLastHour)}>
            {(queue.errorRateLastHour * 100).toFixed(2)}%
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={
          highlight ? 'font-mono text-red-700 font-semibold' : 'font-mono'
        }
      >
        {value}
      </span>
    </div>
  );
}
